import { useSession } from 'next-auth/react';
import { PropsWithChildren, createContext, use, useCallback } from 'react';
import useSWR, { SWRResponse } from 'swr';

import { apiScopes } from '../api/apiScopes';
import { useScopedAPI } from '../api/useScopedAPI';
import { env } from '../env';
import {
	IProfile,
	Profile,
	SnowCountry,
	SnowLocation,
	SnowProfile,
	SnowUser,
} from '../types/Profile';

const copyPropertyValues = <T extends Record<string, unknown>, K extends keyof T>(
	copyTo: T | null,
	copyFrom: T | null,
	keys: K[],
) => {
	if (!copyTo || !copyFrom) {
		return;
	}

	keys.forEach((key) => {
		const keyValue = copyFrom[key];
		if (keyValue !== undefined && keyValue !== null) {
			copyTo[key] = keyValue;
		}
	});
};

export type IProfileContext = SWRResponse<IProfile, Error> | null;

export const ProfileContext = createContext<IProfileContext>(null);

const useFetchProfile = () => {
	const { get: getExplorer } = useScopedAPI([apiScopes.orgExplorer.user_impersonation]);

	return useCallback(
		async (userEmail: string) => {
			// process GetUserExplorerCard
			const explorerResponse = await getExplorer<Partial<IProfile>>(
				`${env.orgExplorerApiUrl}GetUserExplorerCard?email=${userEmail}&team=0&sessionId=`,
			);
			if (!explorerResponse) {
				// eslint-disable-next-line no-console
				console.error('explorerResponse is empty');
				return;
			}
			const result = {} as IProfile;
			copyPropertyValues(result, explorerResponse, Object.keys(new Profile()) as (keyof Profile)[]);
			return result;
		},
		[getExplorer],
	);
};

export const ProfileProvider = ({ children }: PropsWithChildren) => {
	const session = useSession();

	const fetchProfile = useFetchProfile();

	const { get: getMarketplace } = useScopedAPI([apiScopes.marketplace.user_impersonation]);

	const fetcher = async (userEmail: string): Promise<IProfile> => {
		const profile = session.data?.isTestUser
			? ({ email: userEmail } as IProfile)
			: await fetchProfile(userEmail);

		// process Show Profile
		const snowProfileResponse: any = session.data?.isTestUser
			? null
			: await getMarketplace(`${env.marketplaceApiUrl}snow/profile?email=${userEmail}`);

		if (typeof snowProfileResponse === 'object' && snowProfileResponse !== null) {
			const snowProfile = new SnowProfile();
			copyPropertyValues(snowProfile, snowProfileResponse, Object.keys(new SnowUser()));
			copyPropertyValues(
				snowProfile.manager,
				snowProfileResponse['manager'],
				Object.keys(new SnowUser()),
			);
			copyPropertyValues(
				snowProfile.location,
				snowProfileResponse['location'],
				Object.keys(new SnowLocation()),
			);
			copyPropertyValues(
				snowProfile.country,
				snowProfileResponse['country'],
				Object.keys(new SnowCountry()),
			);

			// @ts-expect-error
			profile.snowProfile = snowProfile;
		}

		// @ts-expect-error
		return profile;
	};

	const value = useSWR(session.data?.user.email, fetcher);

	return <ProfileContext value={value}>{children}</ProfileContext>;
};

export const useProfile = () => use(ProfileContext);
